import React from 'react';
import { Clock, User, ChevronRight } from 'lucide-react';
import { BlogPost } from '../types';

interface BlogCardProps {
    blog: BlogPost;
    onClick: () => void;
    viewMode: 'grid' | 'list';
}

const BlogCard: React.FC<BlogCardProps> = ({ blog, onClick, viewMode }) => {
    const isFeatured = blog.featured && viewMode === 'grid';

    if (viewMode === 'list') {
        return (
            <button
                type="button"
                onClick={onClick}
                className="group bg-white rounded-2xl border border-gray-200 p-6 flex flex-col md:flex-row gap-6 text-left cursor-pointer hover:shadow-xl hover:border-emerald-200 transition-all"
            >
                <div className="w-full md:w-64 h-48 flex-shrink-0 overflow-hidden rounded-xl">
                    <img
                        src={blog.imageUrl}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                </div>
                <div className="flex flex-col justify-between flex-grow">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider">
                                {blog.category}
                            </span>
                            <span className="text-gray-400 text-sm">{blog.date}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                            {blog.title}
                        </h3>
                        <p className="text-gray-600 line-clamp-2 mb-4">{blog.excerpt}</p>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                                <User size={14} /> {blog.author}
                            </span>
                            <span className="flex items-center gap-1">
                                <Clock size={14} /> {blog.readTime}
                            </span>
                        </div>
                        <span className="text-emerald-600 font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                            Read More <ChevronRight size={18} />
                        </span>
                    </div>
                </div>
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`group cursor-pointer bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 text-left ${
                isFeatured ? 'md:col-span-2 flex flex-col md:flex-row' : ''
            }`}
        >
            <div className={`relative overflow-hidden ${isFeatured ? 'md:w-1/2 h-full min-h-[300px]' : 'h-64'}`}>
                <img
                    src={blog.imageUrl}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-emerald-700 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest shadow-lg">
                        {blog.category}
                    </span>
                </div>
                {isFeatured && (
                    <div className="absolute top-4 right-4">
                        <span className="bg-emerald-600 text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest shadow-lg flex items-center gap-1">
                            Featured
                        </span>
                    </div>
                )}
            </div>

            <div className={`p-8 flex flex-col justify-between ${isFeatured ? 'md:w-1/2' : ''}`}>
                <div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 font-bold mb-4 uppercase tracking-tighter">
                        <span className="flex items-center gap-1">
                            <User size={14} /> {blog.author}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                            <Clock size={14} /> {blog.readTime}
                        </span>
                    </div>
                    <h3
                        className={`font-black text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors leading-tight ${
                            isFeatured ? 'text-3xl' : 'text-xl'
                        }`}
                    >
                        {blog.title}
                    </h3>
                    <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">{blog.excerpt}</p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <span className="text-sm text-gray-400 font-medium">{blog.date}</span>
                    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-all">
                        <ChevronRight size={20} />
                    </div>
                </div>
            </div>
        </button>
    );
};

export default BlogCard;
